import React, { useEffect } from 'react';
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import { SelectInput } from "./form/SelectInput";
import { selectCurrentProjectId } from "../store/models/Project";
import { selectCurrentUserId } from "../store/models/User";
import Ai, { selectAllRagChatbots, selectRagChatbot } from "../store/models/Ai";
import { Urls } from "../constants";
import { PlusIcon } from './Svg';
import { selectMobileLayout } from "../store/UIState";
import { pushTo } from '../store/history';
import { mergeUrl } from '../utils/url';

const selector = createSelector(
  selectCurrentProjectId,
  selectCurrentUserId,
  (state, chatbotId) => selectRagChatbot(state, selectCurrentProjectId(state), chatbotId),
  (state) => selectAllRagChatbots(state, selectCurrentProjectId(state)),
  selectMobileLayout,
  (projectId, userId, currentChatbot, chatbots, isMobile) => ({
    projectId,
    userId,
    currentChatbot,
    chatbots: _.values(chatbots),
    isMobile
  })
);

const ChatbotLabel = ({ chatbot }) => {
  return (
    <div className={"ChatbotLabel"}>
      <div className="ChatbotLabel__sub">Chatbot</div>
      <div className={"ChatbotLabel__name"}>
        {chatbot?.name || 'Select Chatbot'}
      </div>
    </div>
  );
};

const SelectChatbotOption = ({ chatbot }) => {
  return (
    <div className={"SelectProjectModal__SelectOption"}>
      <div className={"SelectProjectModal__SelectOption__name"}>{chatbot.name || 'RAG Chatbot'}</div>
    </div>
  );
};

const CreateNewChatbotOption = () => {
  return (
    <div className="SelectProjectModal__SelectOption CreateNewOption">
      <PlusIcon className="CreateNewOption__icon" />
      <span>Create new chatbot</span>
    </div>
  );
};


export const ChatbotDropdown = () => {
  const dispatch = useDispatch();
  
  // Extract chatbot ID from URL path since useParams won't work due to redirect
  const getCurrentChatbotId = () => {
    const pathname = window.location.pathname;
    // Convert URL pattern to regex by replacing :id with capturing group
    const urlPattern = Urls.MINI_DASHBOARD_CHATBOT.replace(':id', '([^/]+)');
    const regex = new RegExp(urlPattern);
    const match = pathname.match(regex);
    return match ? match[1] : null;
  };
  
  const currentChatbotId = getCurrentChatbotId();

  const { projectId, userId, currentChatbot, chatbots, isMobile } = useSelector(state => 
    selector(state, currentChatbotId)
  );

  const sortedChatbots = [...chatbots]
    .sort((a, b) => (a.name || 'RAG Chatbot').localeCompare(b.name || 'RAG Chatbot'));

  const chatbotOptions = [
    ...sortedChatbots.map(chatbot => ({
      label: <SelectChatbotOption chatbot={chatbot} />,
      value: chatbot.id
    })),
  ];

  if (!isMobile) {
    chatbotOptions.unshift({ label: "Chatbots", value: null, isDisabled: true });
  }

  useEffect(() => {
    if (projectId && userId) {
      fetchChatbots();
    }
  }, [projectId, userId]);

  const fetchChatbots = async () => {
    try {
      await dispatch(Ai.actions.fetchRagChatbots(projectId));
    } catch (e) {
      console.error('Failed to fetch chatbots:', e);
    }
  };

  const onSelectChatbot = async (option) => {
    const chatbotId = option.value;

    pushTo(mergeUrl(Urls.MINI_DASHBOARD_CHATBOT, { id: chatbotId }));
  };

  if (!projectId || !userId) {
    return null;
  }

  if (chatbots.length === 0) {
    return null;
  }

  const displayChatbot = currentChatbot || (currentChatbotId ? chatbots.find(c => c.id === currentChatbotId) : null);
  

  return (
    <div className={"ChatbotDropdown"}>
      <div className={"ChatbotDropdown__Dropdown"}>
        <div className={"ChatbotDropdown__DropdownLabel"}>Chatbot</div>
        <SelectInput 
          value={{ 
            label: <ChatbotLabel chatbot={displayChatbot} />, 
            value: displayChatbot?.id 
          }}
          options={chatbotOptions}
          styles={isMobile ? mobileSelectStyles : selectStyles}
          onChange={onSelectChatbot} 
        />
      </div>
    </div>
  );
};

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    borderColor: 'transparent',
    borderRadius: '4px',
    color: '#8A8FB5',
    outline: 'none',
    minHeight: 'none',
    boxShadow: 'none',
    ':hover': {
      borderColor: '#18C99D',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled ? '#131722' :
      state.isFocused ? 'rgba(24, 201, 157, 0.1)' : '#131722',
    color: state.isDisabled ? '#FFFFFF' :
      state.isFocused ? '#18C99D' :
        state.isSelected ? '#18C99D' : '#8A8FB5',
    fontFamily: 'Gilroy',
    paddingLeft: '10px',
    fontWeight: '500',
    fontSize: '14px',
    paddingTop: '11.5px',
    paddingBottom: '11.5px',
    height: 'auto',
    position: 'relative',
    cursor: state.isDisabled ? 'default' : 'pointer',
    ':hover': {
      backgroundColor: state.isDisabled ? '#131722' : 'rgba(24, 201, 157, 0.1)',
    },
  }),
  singleValue: (provided, state) => ({
    ...provided,
    borderColor: state.isSelected ? '#18C99D' : 'transparent',
    color: '#8A8FB5',
    fontFamily: 'Gilroy',
    fontWeight: '500',
    fontSize: '14px',
    outline: 'none'
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#8A8FB5',
    marginRight: '0',
    marginLeft: '4px',
    ':hover': {
      color: '#8A8FB5',
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    borderRadius: '4px',
    scrollbarColor: '#3D4463 #131722',
    scrollbarWidth: 'thin',
    width: '250px',
    zIndex: 3
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '500px',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: '#131722',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#3D4463',
      borderRadius: '4px',
    }
  }),
}

const mobileSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#202432',
    borderColor: 'transparent',
    borderRadius: '0',
    color: '#8A8FB5',
    outline: 'none',
    minHeight: 'none',
    height: '50px',
    boxShadow: 'none',
    ':hover': {
      borderColor: 'transparent',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled ? '#202432' :
      state.isFocused ? 'rgba(24, 201, 157, 0.1)' : '#202432',
    color: state.isDisabled ? '#FFFFFF' :
      state.isFocused ? '#18C99D' :
        state.isSelected ? '#18C99D' : '#8A8FB5',
    fontFamily: 'Gilroy',
    paddingLeft: '10px',
    fontWeight: '500',
    fontSize: '14px',
    paddingTop: '11.5px',
    paddingBottom: '11.5px',
    height: 'auto',
    position: 'relative',
    cursor: state.isDisabled ? 'default' : 'pointer',
    ':hover': {
      backgroundColor: state.isDisabled ? '#202432' : 'rgba(24, 201, 157, 0.1)',
    },
  }),
  singleValue: (provided, state) => ({
    ...provided,
    borderColor: state.isSelected ? '#18C99D' : 'transparent',
    color: '#8A8FB5',
    fontFamily: 'Gilroy',
    fontWeight: '500',
    fontSize: '14px',
    outline: 'none',
  }),
  indicatorSeparator: (provided, state) => ({
    ...provided,
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#8A8FB5',
    marginRight: '0',
    marginLeft: '4px',
    ':hover': {
      color: '#8A8FB5',
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    backgroundColor: '#131722',
    borderRadius: '4px',
    scrollbarColor: '#3D4463 #131722',
    scrollbarWidth: 'thin',
    width: '100vw',
    marginTop: '0',
    zIndex: 1000,
    position: 'fixed',
    left: '0',
    top: '13rem',
    right: '0',
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '500px',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: '#131722',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#3D4463',
      borderRadius: '4px',
    }
  }),
}; 